/*
 * Central Zustand store — the single source of truth that replaces the old
 * global mutable `state` object from taskpane.js. Fields mirror that object 1:1
 * (minus ephemeral polling handles, which now live as refs inside
 * useLiveFeedback). `patch()` mirrors the old `state.x = y` mutation style so
 * ported imperative flows stay readable; shell actions are typed explicitly.
 */

import { create } from "zustand";
import type {
  AuthState,
  CatalogPreferences,
  CatalogSections,
  DocSuggestion,
  DocReviewFinding,
  KarlAssists,
  KarlCapability,
  KarlCaseStatus,
  KarlVerdict,
  Lang,
  LiveSuggestion,
  Occurrence,
  OccurrenceEdit,
  PreferenceValues,
  AiSuggestionState,
  ReportTarget,
  Repetition,
  Rule,
  RuleDependencies,
  SessionUser,
  SubscriptionOverview,
  TabId,
} from "./types";

const LANG_KEY = "sw_addin_lang";

function initialLang(): Lang {
  const stored = typeof localStorage !== "undefined" ? localStorage.getItem(LANG_KEY) : null;
  return stored === "el" ? "el" : "en";
}

export interface AppData {
  /* shell */
  activeTab: TabId;
  loading: boolean;

  /* i18n */
  lang: Lang;

  /* auth */
  authState: AuthState;
  pendingEmail: string;
  sessionUser: SessionUser | null;
  subscription: SubscriptionOverview | null;

  /* books (populated once a token is chosen) */
  token: string; // optional legacy raw x-service-token value
  rulesConfigured: boolean;
  rules: Rule[];
  catalogPreferences: CatalogPreferences;
  catalogSections: CatalogSections;
  dependencies: RuleDependencies;
  preferences: PreferenceValues;
  selectedRuleIds: string[];
  selectedConfusionRuleIds: string[];
  selectedPresetId: string;

  /* Karl capability advertised by the rule catalog */
  karlCapability: KarlCapability | null;

  /* selection scan */
  originalSelectionText: string;
  workingSelectionText: string;
  occurrences: Occurrence[];
  currentOccurrenceIndex: number;
  /* Replacements this review already wrote into the selection, by index. */
  occurrenceEdits: Record<string, OccurrenceEdit>;

  /* Karl answers and decisions, kept across panel switches */
  karlVerdicts: Record<string, KarlVerdict>;
  karlCaseStatuses: Record<string, KarlCaseStatus>;
  karlReviewed: Record<string, boolean>;
  karlAccepted: Record<string, "keep" | "change">;
  karlAssists: KarlAssists | null;

  /* doc suggestions */
  docSuggestions: DocReviewFinding[];
  currentDocSuggestionIndex: number;
  originalParagraphTexts: Record<number, string>;
  appliedChangesInParagraph: Record<number, DocSuggestion[]>;

  /* doc suggestions — review of the current selection only */
  reviewSelectionSuggestions: DocReviewFinding[];
  currentReviewSelectionIndex: number;
  reviewSelectionOriginalText: string;
  reviewSelectionWorkingText: string;
  reviewSelectionShift: number;

  /* live feedback */
  liveEnabled: boolean;
  liveParagraphText: string;
  liveSuggestions: LiveSuggestion[];
  currentLiveSuggestionIndex: number;
  liveIsScanning: boolean;
  liveStatus: { message: string; variant: "idle" | "error" };

  /* live feedback — repetition detection config */
  liveRepetitionEnabled: boolean;
  liveRepetitionWindow: number; // margin of words (windowSize): 1–250
  liveRepetitionFrequency: number; // frequency (minOccurrences): 2–20
  liveRepetitions: Repetition[];
  liveRepAiSuggestions: Record<string, AiSuggestionState>;
  liveRepRemainingQuota: number | null;

  /* false positive report modal */
  reportModalOpen: boolean;
  reportModalTarget: ReportTarget;
}

export interface AppActions {
  patch: (partial: Partial<AppData>) => void;
  setLang: (lang: Lang) => void;
  toggleLang: () => void;
  setActiveTab: (tab: TabId) => void;
  setLoading: (loading: boolean) => void;
  /** Reset all book/auth/session state (keeps language). Used on logout. */
  resetSession: () => void;
}

export type AppStore = AppData & AppActions;

const initialData: Omit<AppData, "lang"> = {
  activeTab: "batch",
  loading: false,

  authState: "logged-out",
  pendingEmail: "",
  sessionUser: null,
  subscription: null,

  token: "",
  rulesConfigured: false,
  rules: [],
  catalogPreferences: {},
  catalogSections: {},
  dependencies: {},
  preferences: {},
  selectedRuleIds: [],
  selectedConfusionRuleIds: [],
  selectedPresetId: "",

  karlCapability: null,

  originalSelectionText: "",
  workingSelectionText: "",
  occurrences: [],
  currentOccurrenceIndex: -1,
  occurrenceEdits: {},

  karlVerdicts: {},
  karlCaseStatuses: {},
  karlReviewed: {},
  karlAccepted: {},
  karlAssists: null,

  docSuggestions: [],
  currentDocSuggestionIndex: -1,
  originalParagraphTexts: {},
  appliedChangesInParagraph: {},

  reviewSelectionSuggestions: [],
  currentReviewSelectionIndex: -1,
  reviewSelectionOriginalText: "",
  reviewSelectionWorkingText: "",
  reviewSelectionShift: 0,

  liveEnabled: false,
  liveParagraphText: "",
  liveSuggestions: [],
  currentLiveSuggestionIndex: -1,
  liveIsScanning: false,
  liveStatus: { message: "", variant: "idle" },

  liveRepetitionEnabled: false,
  liveRepetitionWindow: 50,
  liveRepetitionFrequency: 2,
  liveRepetitions: [],
  liveRepAiSuggestions: {},
  liveRepRemainingQuota: null,

  reportModalOpen: false,
  reportModalTarget: null,
};

export const useStore = create<AppStore>((set) => ({
  ...initialData,
  // Start in a loading state so the React overlay covers the cold-start
  // session probe (see App bootstrap) with no flash of empty UI.
  loading: true,
  lang: initialLang(),

  patch: (partial) => set(partial),

  setLang: (lang) => {
    if (typeof localStorage !== "undefined") localStorage.setItem(LANG_KEY, lang);
    set({ lang });
  },

  toggleLang: () =>
    set((state) => {
      const lang: Lang = state.lang === "en" ? "el" : "en";
      if (typeof localStorage !== "undefined") localStorage.setItem(LANG_KEY, lang);
      return { lang };
    }),

  setActiveTab: (activeTab) => set({ activeTab }),

  setLoading: (loading) => set({ loading }),

  resetSession: () => set({ ...initialData }),
}));
